import { useCallback, useEffect, useMemo, useState } from 'react';

import { PopOver } from '@/components/PopOver';
import { CategoryWidget } from '@/widgets/CategoryWidget';

import { CategoryDropdownContext } from './CategoryDropdownContext';

import type { CategoryDropdownContextValue, CategoryDropdownProps } from './types';

/**
 * Category 메뉴 dropdown — PopOver + CategoryWidget 자동 합성 + 다중 trigger 캡슐화.
 *
 * # 책임
 *
 * - open state 관리 (controlled / uncontrolled)
 * - Trigger click 시 toggle
 * - PopOver + CategoryWidget content 자동 mount
 * - Trigger 자동 등록 — 단일 trigger 케이스는 anchorEl 명시 불필요
 *
 * # anchor 결정
 *
 * 1. `anchorEl` prop 명시 시 그쪽 우선 (다중 trigger / 비즈니스 분기 케이스).
 * 2. `anchorEl` 미명시 시 자동 등록된 trigger 의 element 사용 (단일 trigger 케이스).
 * 3. 2개 이상 등록되면 자동 anchor 비활성 + dev warn — 호스트가 anchorEl 명시 필요.
 *
 * 단일 trigger 케이스 (자동):
 * ```tsx
 * <CategoryDropdown>
 *   <CategoryDropdown.Trigger>
 *     <button>...</button>
 *   </CategoryDropdown.Trigger>
 * </CategoryDropdown>
 * ```
 *
 * 다중 trigger / 동적 anchor (display-site 의 isScrolled 분기 등):
 * ```tsx
 * <CategoryDropdown anchorEl={isScrolled ? topAnchor : bottomAnchor}>
 *   <Header.CategoryTrigger ref={setTopAnchor} />
 *   <CategoryDropdown.Trigger>
 *     <button ref={setBottomAnchor}>...</button>
 *   </CategoryDropdown.Trigger>
 * </CategoryDropdown>
 * ```
 *
 * # 미지원 (의도된 design 결정)
 *
 * - mobile 자동 비활성 (`disableOnMobile` 등) — 호스트가 trigger visibility 로 자체 제어
 * - breakpoint 변경 시 controlled open 자동 close — 호스트가 `onBreakpointChange` 에서 명시 close (Header context 비의존 유지)
 *   (uncontrolled 는 anchor 가 사라지거나 숨겨지면 내부에서 자동 close)
 * - Suspense — CategoryWidget 의 정책 그대로 따름
 */
function CategoryDropdownRoot({
  children,
  open: controlledOpen,
  onOpenChange,
  onAction,
  contentClassName,
  zIndex,
  anchorEl,
}: CategoryDropdownProps) {
  // ── open state (controlled / uncontrolled) ────────────────────
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpenControlled = controlledOpen !== undefined;
  const open = isOpenControlled ? controlledOpen : internalOpen;

  // ── Trigger 자동 등록 ─────────────────────────────────────────
  // Trigger 들이 mount 시 자기 element 를 등록, unmount 시 해제.
  // *useState* (useRef 가 아님) — 등록 변동이 *anchor 결정* 에 영향 → re-render 필요.
  // *배열* (Set 도 가능) — `length === 1` 체크가 코드 의도("정확히 하나")를 직설적으로 표현.
  const [registeredTriggers, setRegisteredTriggers] = useState<readonly HTMLElement[]>([]);

  const registerTrigger = useCallback((el: HTMLElement) => {
    // idempotent — 동일 element 재등록 무시 (strict mode / re-mount 안전).
    setRegisteredTriggers((prev) => (prev.includes(el) ? prev : [...prev, el]));
    return () => {
      setRegisteredTriggers((prev) => prev.filter((e) => e !== el));
    };
  }, []);

  // ── anchor 결정 ───────────────────────────────────────────────
  // anchorEl prop 의 세 상태 구분:
  //   undefined → 호스트가 prop 자체를 *안 줌* → 자동 fallback 활성
  //   null      → 호스트가 *명시적으로* "anchor 없음" → 자동 fallback 안 함
  //   element   → 호스트 명시 anchor
  // 호스트의 흔한 useState 초기값 (null) 도 "명시" 로 취급 — 자동 fallback 의도 안 함.
  const isAnchorExplicit = anchorEl !== undefined;

  // 자동 anchor 후보: register 가 *정확히 1개* 일 때만.
  //   0개 → 자동화의 *대상이 없음* (호스트가 Trigger 미사용)
  //   2개+ → *어느 trigger 인지 모호* → silent ambiguity 회피, 자동화 비활성
  const autoAnchor = registeredTriggers.length === 1 ? registeredTriggers[0] : null;

  // 최종 anchor — 명시 우선, 미명시 시 자동 fallback.
  const resolvedAnchor = isAnchorExplicit ? (anchorEl ?? null) : autoAnchor;

  // ── dev warn ──────────────────────────────────────────────────
  // 두 가지 케이스만 경고 — *호스트가 자동 fallback 을 기대하는데 실패하는* 경우.
  // (anchorEl 명시 호스트는 호스트 책임이라 추가 경고 없음.)
  // useEffect — 조건 변경 시에만 1회 발생, render-time side-effect 회피.
  const triggerCount = registeredTriggers.length;
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    if (!isAnchorExplicit && triggerCount > 1) {
      // eslint-disable-next-line no-console
      console.warn(
        '[core-ui] CategoryDropdown detected multiple registered triggers ' +
          `(${triggerCount}). Auto-anchor is disabled — provide \`anchorEl\` prop ` +
          'to disambiguate which trigger should anchor the popover.',
      );
    }
  }, [isAnchorExplicit, triggerCount]);

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isOpenControlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [isOpenControlled, onOpenChange],
  );

  const hasNoAnchor = open && resolvedAnchor === null;
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    if (hasNoAnchor) {
      // eslint-disable-next-line no-console
      console.warn(
        '[core-ui] CategoryDropdown is open but has no anchor. ' +
          'Provide `anchorEl` prop or wrap a trigger in <CategoryDropdown.Trigger> / use <Header.CategoryTrigger>.',
      );
    }
  }, [hasNoAnchor]);

  // ── uncontrolled auto-close (anchor 유효성/가시성 감시) ─────────────
  // 의도:
  // - controlled: host 가 소유 (기존 contract 유지)
  // - uncontrolled: host state 없이도 viewport/visibility 변화에 안전하게 close
  useEffect(() => {
    if (isOpenControlled || !open) return;

    const isAnchorUsable = (anchor: HTMLElement | null) => {
      if (!anchor) return false;
      if (!anchor.isConnected) return false;
      // getClientRects() 는 자신/조상에 display:none 이 있으면 빈 배열 반환 (layout 제외).
      // visibility:hidden 은 layout 에 참여해 rects 가 존재하므로 별도 체크.
      if (anchor.getClientRects().length === 0) return false;
      if (window.getComputedStyle(anchor).visibility === 'hidden') return false;
      return true;
    };

    const syncOpenWithAnchor = () => {
      if (!isAnchorUsable(resolvedAnchor)) setOpen(false);
    };

    // effect 시작 시 1회 즉시 검증 (breakpoint 직후 stale open 방지)
    syncOpenWithAnchor();

    // scroll 은 고빈도 이벤트라 rAF 로 프레임당 최대 1회로 제한.
    // resize / orientationchange 는 저빈도라 직접 호출.
    let rafId: ReturnType<typeof requestAnimationFrame> | null = null;
    const onScroll = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        syncOpenWithAnchor();
      });
    };

    window.addEventListener('resize', syncOpenWithAnchor);
    window.addEventListener('orientationchange', syncOpenWithAnchor);
    window.addEventListener('scroll', onScroll, true);

    return () => {
      window.removeEventListener('resize', syncOpenWithAnchor);
      window.removeEventListener('orientationchange', syncOpenWithAnchor);
      window.removeEventListener('scroll', onScroll, true);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [isOpenControlled, open, resolvedAnchor, setOpen]);

  // ── handlers ──────────────────────────────────────────────────
  const handleTriggerClick = useCallback(() => {
    setOpen(!open);
  }, [open, setOpen]);

  const close = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const ctxValue = useMemo<CategoryDropdownContextValue>(
    () => ({ open, handleTriggerClick, close, registerTrigger }),
    [open, handleTriggerClick, close, registerTrigger],
  );

  return (
    <CategoryDropdownContext.Provider value={ctxValue}>
      {children}
      <PopOver anchorEl={resolvedAnchor} onOpenChange={setOpen} open={open}>
        <PopOver.Content className={contentClassName} zIndex={zIndex}>
          <CategoryWidget onAction={onAction} />
        </PopOver.Content>
      </PopOver>
    </CategoryDropdownContext.Provider>
  );
}

CategoryDropdownRoot.displayName = 'CategoryDropdown';

export { CategoryDropdownRoot };
